/**
 * SuperAdminStoreGate — a small gate shown at the top of store-scoped admin
 * pages (Coupons & Marketing, Deal Baazar, Homepage Layout, etc.) whenever a
 * SUPER_ADMIN lands on `/admin/platform/*` without a store selected.
 *
 * Behaviour:
 *   • For SUPER_ADMIN with `store_id` already resolved (i.e. they're on
 *     `/admin/{slug}/*`): pass-through, render children.
 *   • For SUPER_ADMIN on `/admin/platform/*`: fetch the store list and show a
 *     dropdown. Choosing a store navigates to `/admin/{picked-slug}/{tail}`
 *     which triggers AppShell's slug → store_id resolution.
 *   • For every other role: pass-through (their store is always bound).
 *
 * This prevents STORE_CONTEXT_REQUIRED 403s from firing on data loads that
 * strictly require a tenant scope.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth, isSuperAdmin } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Store as StoreIcon, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function SuperAdminStoreGate({ children, pageTitle, pageSubtitle, autoPickPrimary = false }) {
  const { user } = useAuth();
  const { storeSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Non-SA users always have a store scope — pass through.
  if (!isSuperAdmin(user)) return children;
  // SA on `/admin/platform/*` — either auto-pick the primary store OR show the picker.
  if (!storeSlug || storeSlug === "platform") {
    if (autoPickPrimary) {
      return (
        <AutoPickPrimary
          onPick={(slug) => {
            const tail = location.pathname.replace(/^\/admin\/platform/, "") || "";
            navigate(`/admin/${slug}${tail}`, { replace: true });
          }}
          pageTitle={pageTitle}
          pageSubtitle={pageSubtitle}
        />
      );
    }
    return (
      <StorePicker
        pageTitle={pageTitle}
        pageSubtitle={pageSubtitle}
        onPick={(slug) => {
          const tail = location.pathname.replace(/^\/admin\/platform/, "") || "";
          navigate(`/admin/${slug}${tail}`, { replace: true });
        }}
      />
    );
  }
  // SA on `/admin/{slug}/*` — hold children until AppShell has resolved the
  // slug and stashed the tenant id in localStorage. Prevents a race where the
  // child page's initial data load fires without X-Store-Id and 403s.
  return <SlugScopedGate pageTitle={pageTitle} pageSubtitle={pageSubtitle}>{children}</SlugScopedGate>;
}

/** Fetches the primary/first-active store and hands its slug back via onPick.
 *  Uses the public `/store/settings/public` endpoint which resolves to the
 *  same store the customer storefront reads — guarantees the SA edits the
 *  live-facing tenant, not some throwaway test store. */
function AutoPickPrimary({ onPick, pageTitle, pageSubtitle }) {
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1) Ask the public storefront endpoint which store it renders — this
        //    is the true "primary" as far as customers are concerned.
        const { data: pub } = await api.get("/store/settings/public");
        const primarySlug = pub?.slug;
        if (primarySlug) {
          if (!cancelled) onPick(primarySlug);
          return;
        }
        // 2) Fallback: pick the oldest active store from the admin listing.
        const { data } = await api.get("/stores?limit=100");
        const items = data?.items || [];
        const activeSorted = items
          .filter((s) => s.status === "active")
          .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
        const primary = activeSorted[0] || items[0];
        if (!primary) {
          if (!cancelled) setError("No stores found on this platform.");
          return;
        }
        if (!cancelled) onPick(primary.slug);
      } catch (e) {
        if (!cancelled) setError(errMsg(e));
      }
    })();
    return () => { cancelled = true; };
  }, [onPick]);
  return (
    <div className="p-6 md:p-8" data-testid="sa-auto-pick">
      {pageTitle && <PageHeader title={pageTitle} subtitle={pageSubtitle} />}
      {error ? (
        <Card className="max-w-xl border-red-200 bg-red-50/60">
          <CardContent className="p-6 text-sm text-red-700">{error}</CardContent>
        </Card>
      ) : (
        <div className="flex items-center gap-3 text-slate-600 text-sm" data-testid="sa-auto-pick-loading">
          <Skeleton className="h-4 w-4 rounded-full" /> Loading primary store…
        </div>
      )}
    </div>
  );
}

function SlugScopedGate({ children, pageTitle, pageSubtitle }) {
  const [ready, setReady] = useState(() => !!localStorage.getItem("super_admin_store_id"));
  useEffect(() => {
    if (ready) return;
    const t = setInterval(() => {
      if (localStorage.getItem("super_admin_store_id")) {
        setReady(true);
        clearInterval(t);
      }
    }, 100);
    return () => clearInterval(t);
  }, [ready]);
  if (!ready) {
    return (
      <div className="p-6 md:p-8" data-testid="sa-slug-wait">
        {pageTitle && <PageHeader title={pageTitle} subtitle={pageSubtitle} />}
        <Skeleton className="h-10 w-72 mb-4" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }
  return children;
}

function StorePicker({ pageTitle, pageSubtitle, onPick }) {
  const [stores, setStores] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/stores?limit=100");
        if (!cancelled) setStores(data.items || []);
      } catch (e) {
        if (!cancelled) toast.error(errMsg(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="p-6 md:p-8" data-testid="sa-store-gate">
      {pageTitle && (
        <PageHeader title={pageTitle} subtitle={pageSubtitle} />
      )}

      <Card className="max-w-xl border-amber-200 bg-amber-50/60">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 grid place-items-center shrink-0">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold">Select a store to continue</div>
              <p className="text-sm text-muted-foreground mt-1">
                This page manages settings for a specific store. Pick which store you want to configure.
              </p>
            </div>
          </div>

          {loading ? (
            <Skeleton className="h-10 w-full" />
          ) : !stores || stores.length === 0 ? (
            <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4 text-center">
              No stores found on the platform yet.
            </div>
          ) : (
            <Select onValueChange={(slug) => onPick(slug)}>
              <SelectTrigger className="w-full" data-testid="sa-store-picker">
                <SelectValue placeholder="Choose a store…" />
              </SelectTrigger>
              <SelectContent>
                {stores.map((s) => (
                  <SelectItem key={s.id} value={s.slug} data-testid={`sa-store-option-${s.slug}`}>
                    <span className="inline-flex items-center gap-2">
                      <StoreIcon className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="font-medium">{s.name}</span>
                      <span className="text-xs text-muted-foreground">/{s.slug}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
